import { useRouter } from 'vue-router'
import {
  Channel,
  pages
} from 'frontendcommon-3'
import { useShellStore } from '@/store'
import { microUtils } from '@/utils'
import LayoutPrintShell from '@/views/LayoutPrintShell'
import * as Sentry from '@sentry/browser'

const {
  PageManteinance,
  PageNotFound
} = pages

export function useMicros () {
  const router = useRouter()
  const shellStore = useShellStore()

  function updateRouter (route, firstPage = false) {
    if (firstPage) {
      window.history.replaceState({ }, '', route)
      router.replace(route)
    } else {
      window.history.pushState({ }, '', route)
      router.push(route)
    }
  }

  function changeRoute (route, firstPage = false) {
    updateRouter(route, firstPage)
    const microId = router.resolve(route).meta.id
    Channel.emitChangeRoute(microId, window.location.pathname + window.location.search)
  }

  function changeMicro (route, firstPage = false) {
    updateRouter(route, firstPage)
  }

  function changeMicroById (microId, firstPage = false) {
    const item = shellStore.micros.find((item) => item.id === microId)
    if (item) updateRouter(`/${item.activation_path}`, firstPage)
  }

  function setRoutes () {
    // Remove existing routes
    router.getRoutes().forEach((route) => {
      if (route.name && route.name.startsWith('root')) return
      router.removeRoute(route.name)
    })
    // Add new routes
    const forceEnable = localStorage.getItem('force_enable') === '1'
    shellStore.micros.forEach(element => {
      if (!element.activation_path) return
      if (element.disabled && !forceEnable) {
        router.addRoute('root', createDisabledRoute(element))
      } else if (element.layout === 'print') {
        router.addRoute(createPrintRoute(element))
      } else {
        router.addRoute('root', createRoute(element))
      }
    })
    router.addRoute('root', create404Route())
  }

  async function loadMicro (micro, loadModules = false) {
    if (typeof micro === 'string') micro = shellStore.getMicroById(micro)
    if (!micro) return Promise.resolve(null)
    if (micro.isLoading) return micro.promise
    if (!micro.isLoaded) {
      micro.promise = startLoadMicro(micro, loadModules)
      return micro.promise
    }
    const moduleKey = loadModules ? 'modules' : 'module'
    micro[moduleKey] = micro[moduleKey] || (await microUtils.loadMicroApp(micro.manifest.scope, micro.manifest[moduleKey]))
    window.dispatchEvent(new CustomEvent(`${micro.id}:loaded`, { detail: micro }))
    return micro[moduleKey]
  }

  async function startLoadMicro (micro, loadModules = false) {
    try {
      micro.isLoading = true
      micro.manifest = await microUtils.loadMicroManifest(micro.baseUrl)
      // Load dependencies
      try {
        await loadMicroDependendices(micro.manifest.microDependecies || [])
      } catch (err) {
        console.error(err)
        console.log('error loading dependencies', micro.id, micro.baseUrl)
      }
      // Load Script
      const mainFile = micro.manifest.mainFile
      const scriptUrl = micro.manifest[mainFile]
      await microUtils.loadMicroScript(scriptUrl)
      // Load modules
      const moduleKey = loadModules ? 'modules' : 'module'
      micro[moduleKey] = await microUtils.loadMicroApp(micro.manifest.scope, micro.manifest[moduleKey])
      // Change status
      micro.isLoading = false
      micro.isLoaded = true
      micro.promise = null
      window.dispatchEvent(new CustomEvent(`${micro.id}:loaded`, { detail: micro }))
      return micro[moduleKey]
    } catch (error) {
      micro.isLoading = false
      window.dispatchEvent(new CustomEvent(`${micro.id}:error`, { detail: error }))
      micro.promise = Promise.reject(new Error(`Rejected promis for -> ${micro.id}:error`)).catch(() => {})
    }
  }

  function loadMicroDependendices (microDependendices) {
    const promises = []
    for (const depe of microDependendices) {
      const micro = shellStore.getMicroById(depe)
      // avoid load undefined dependencies
      if (micro) {
        promises.push(loadMicro(micro, true))
      } else {
        const message = `AppShell: loadMicroDependendices with invalid microId "${depe}". Micro NOT found`
        const micros = shellStore.micros
        console.error(message, JSON.stringify({ microDependendices, micros }))
        Sentry.captureMessage(message)
      }
    }
    return Promise.all(promises)
  }

  async function destroyMicro (app) {
    if (app && app.asyncDestroy) {
      await app.asyncDestroy()
    } else if (app && app.$destroy) {
      app.$destroy()
    } else if (app && app.unmount) {
      app.unmount()
    }
  }

  return {
    changeMicro,
    setRoutes,
    loadMicroDependendices,
    loadMicro,
    destroyMicro,
    changeRoute,
    updateRouter,
    changeMicroById
  }
}

function createRoute (element) {
  return {
    path: `/${element.activation_path}/:pathMatch(.*)*`,
    name: element.name || element.id,
    meta: { requireAuth: true, ...element },
    component: () => import(/* webpackChunkName: "micro-view" */ '../views/MicroView')
  }
}

function createPrintRoute (element) {
  const route = createRoute(element)
  return {
    path: `/${element.activation_path}/:pathMatch(.*)*`,
    component: LayoutPrintShell,
    children: [
      {
        ...route,
        path: ''
      }
    ]
  }
}

function createDisabledRoute (element) {
  const route = createRoute(element)
  return {
    ...route,
    component: () => Promise.resolve(PageManteinance)
  }
}

function create404Route () {
  return {
    path: '/:pathMatch(.*)*',
    name: 'error404',
    meta: { requireAuth: true },
    component: () => Promise.resolve(PageNotFound)
  }
}
